import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    Check,
    FileText,
    Leaf,
    Package,
    Loader2,
    Sparkles,
    Send,
} from "lucide-react";
import { LINES, LINE_IDS } from "../data/lines";
import { submitLead } from "../lib/api";
import { toast } from "sonner";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { WhatsAppButton } from "../components/site/WhatsAppButton";
import { CartDrawer } from "../components/site/CartDrawer";
import { useSiteContent, getSection } from "../context/SiteContentContext";

const initialForm = {
    name: "",
    email: "",
    phone: "",
    city: "",
    business_type: "",
    message: "",
};

// Theme presets used by line_details. Frontend maps CMS theme key → these.
const LINE_THEME = {
    orange: {
        color: "#B95C50",
        accentClass: "text-brand-orange",
        bgClass: "bg-brand-orange-soft",
    },
    green: {
        color: "#7A8B76",
        accentClass: "text-brand-green",
        bgClass: "bg-brand-green-soft",
    },
    pink: {
        color: "#CFA059",
        accentClass: "text-[#CFA059]",
        bgClass: "bg-brand-pink",
    },
    yellow: {
        color: "#8691A0",
        accentClass: "text-[#8691A0]",
        bgClass: "bg-brand-yellow",
    },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const resolveImg = (url) => {
    if (!url) return "";
    if (/^https?:/i.test(url)) return url;
    if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
    return url;
};

// Merge CMS line page with static fallback (for missing fields).
function mergeLine(cms, fallback) {
    if (!fallback) return null;
    const f = fallback;
    if (!cms) {
        return {
            id: f.id,
            name: f.name,
            tagline: f.tagline,
            headline: f.headline,
            description: f.description,
            heroImage: f.heroImage,
            color: f.color,
            accentClass: f.accentClass,
            bgClass: f.bgClass,
            flavors: f.flavors,
            ingredients: f.ingredients,
            nutrition: f.nutrition,
            formats: f.formats,
            useCases: f.useCases,
            bestFor: f.bestFor,
        };
    }
    const theme = LINE_THEME[cms.theme] || LINE_THEME.orange;
    return {
        id: f.id,
        name: cms.name || f.name,
        tagline: cms.tagline || f.tagline,
        headline: cms.headline || f.headline,
        description: cms.description || f.description,
        heroImage: resolveImg(cms.hero_image) || f.heroImage,
        color: theme.color,
        accentClass: theme.accentClass,
        bgClass: theme.bgClass,
        flavors: cms.flavors?.length ? cms.flavors : f.flavors,
        ingredients: cms.ingredients?.length ? cms.ingredients : f.ingredients,
        nutrition: cms.nutrition?.length ? cms.nutrition : f.nutrition,
        formats: cms.formats?.length ? cms.formats : f.formats,
        useCases: cms.use_cases?.length ? cms.use_cases : f.useCases,
        bestFor: cms.best_for?.length ? cms.best_for : f.bestFor,
    };
}

export default function ProductLineDetail() {
    const { lineId } = useParams();
    const fallback = LINES[lineId];
    const { content } = useSiteContent();
    const lineDetails = getSection(content, "line_details", {});
    const line = mergeLine(lineDetails[lineId], fallback);
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [lineId]);

    if (!line) return <Navigate to="/" replace />;

    const handle = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error("Nome e email são obrigatórios");
            return;
        }
        setLoading(true);
        try {
            await submitLead({
                ...form,
                interest: "parceiro",
                line_interest: lineId,
            });
            toast.success("Ficha técnica a caminho! Vamos contactar-te em 24h.");
            setDone(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro. Tenta novamente.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid={`line-detail-${line.id}`}>
            <Header />

            {/* Breadcrumb */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                <nav className="flex items-center gap-2 text-sm text-brand-ink-soft">
                    <Link to="/" className="hover:text-brand-green">
                        Início
                    </Link>
                    <span>/</span>
                    <Link to="/#linhas" className="hover:text-brand-green">
                        Linhas
                    </Link>
                    <span>/</span>
                    <span className="font-bold text-brand-ink">{line.name}</span>
                </nav>
            </div>

            {/* Hero */}
            <section className="relative overflow-hidden pt-10 pb-16 md:pt-16 md:pb-24">
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.05] bg-hex-pattern pointer-events-none"
                    style={{ backgroundImage: "url('/brand/pattern-2.png')" }}
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <span
                            className={`inline-flex items-center gap-2 rounded-full ${line.bgClass} px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] ${line.accentClass}`}
                        >
                            <Package className="h-3.5 w-3.5" />
                            Linha {line.name}
                        </span>
                        <h1 className="mt-5 font-display font-medium text-5xl md:text-6xl lg:text-7xl text-brand-ink leading-[0.98] tracking-tight">
                            {line.headline}
                        </h1>
                        <p className="mt-5 text-lg md:text-xl text-brand-ink-soft leading-relaxed max-w-2xl">
                            {line.description}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-2">
                            {line.bestFor.map((tag) => (
                                <span
                                    key={tag}
                                    className={`inline-flex items-center rounded-full ${line.bgClass} ${line.accentClass} px-3 py-1.5 text-xs font-semibold`}
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                        <a
                            href="#ficha-tecnica"
                            data-testid={`line-${line.id}-request-cta`}
                            className={`mt-8 inline-flex items-center gap-2 rounded-full text-white px-7 py-4 text-base font-semibold transition-all shadow-pop hover:translate-y-[-2px]`}
                            style={{ backgroundColor: line.color }}
                        >
                            <FileText className="h-4 w-4" /> Pedir ficha técnica
                        </a>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="relative animate-float">
                            <div
                                className="absolute inset-0 blur-3xl rounded-full"
                                style={{ backgroundColor: `${line.color}35` }}
                            />
                            <img
                                src={line.heroImage}
                                alt={`Linha ${line.name}`}
                                className="relative rounded-2xl shadow-pop w-full h-[420px] lg:h-[520px] object-cover border-8 border-white"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Flavors & Ingredients */}
            <section className="py-16 md:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">
                            Sabores desta linha
                        </span>
                        <h2 className="mt-2 font-display font-medium text-3xl text-brand-ink">
                            {line.flavors.length} variantes disponíveis
                        </h2>
                        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {line.flavors.map((f) => (
                                <li
                                    key={f}
                                    className="flex items-center gap-2.5 rounded-2xl bg-brand-cream px-4 py-3 text-sm text-brand-ink"
                                >
                                    <Leaf className={`h-4 w-4 shrink-0 ${line.accentClass}`} />
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                            Ingredientes
                        </span>
                        <h2 className="mt-2 font-display font-medium text-3xl text-brand-ink">
                            O que vai lá dentro
                        </h2>
                        <ul className="mt-6 space-y-3">
                            {line.ingredients.map((i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span
                                        className={`mt-0.5 shrink-0 h-6 w-6 rounded-full ${line.bgClass} flex items-center justify-center`}
                                    >
                                        <Check className={`h-3.5 w-3.5 ${line.accentClass}`} />
                                    </span>
                                    <span className="text-brand-ink-soft">{i}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8 rounded-2xl bg-brand-ink p-6 text-white">
                            <div className="text-xs uppercase tracking-widest text-brand-orange font-bold">
                                Ficha nutricional
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-4">
                                {line.nutrition.map((n) => (
                                    <div key={n.label}>
                                        <div className="text-[11px] uppercase tracking-widest text-white/60 font-bold">
                                            {n.label}
                                        </div>
                                        <div className="font-display font-medium text-xl mt-0.5">
                                            {n.value}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Formats */}
            <section className="py-16 md:py-20 bg-brand-cream">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">
                            Formatos de embalagem
                        </span>
                        <h2 className="mt-2 font-display font-medium text-3xl md:text-4xl text-brand-ink">
                            Dose a dose ou a granel
                        </h2>
                        <p className="mt-3 text-brand-ink-soft">
                            Adaptamos a embalagem ao teu modelo de operação.
                        </p>
                    </div>
                    <div className="mt-10 grid md:grid-cols-3 gap-5">
                        {line.formats.map((f) => (
                            <article
                                key={f.type}
                                className="rounded-2xl bg-white p-6 shadow-soft hover:shadow-pop transition-all hover:-translate-y-1 ring-1 ring-brand-green/10"
                            >
                                <div
                                    className={`h-12 w-12 rounded-2xl ${line.bgClass} flex items-center justify-center`}
                                >
                                    <Package className={`h-5 w-5 ${line.accentClass}`} />
                                </div>
                                <h3 className="mt-5 font-display font-medium text-xl text-brand-ink">
                                    {f.type}
                                </h3>
                                <div className="mt-1 text-sm text-brand-ink-soft">{f.detail}</div>
                                <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-cream px-3 py-1.5 text-xs font-semibold text-brand-ink">
                                    <Sparkles className="h-3 w-3 text-brand-orange" />
                                    {f.best}
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Use Cases */}
            <section className="py-16 md:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                            Como usar
                        </span>
                        <h2 className="mt-2 font-display font-medium text-3xl md:text-4xl text-brand-ink">
                            3 formas de servir
                        </h2>
                    </div>
                    <div className="mt-10 grid md:grid-cols-3 gap-5">
                        {line.useCases.map((u, i) => (
                            <article
                                key={u.title}
                                className="rounded-2xl bg-brand-cream p-7 relative"
                            >
                                <div
                                    className={`font-display font-medium text-5xl ${line.accentClass} opacity-70`}
                                >
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <h3 className="mt-4 font-display font-medium text-xl text-brand-ink">
                                    {u.title}
                                </h3>
                                <p className="mt-2 text-sm text-brand-ink-soft leading-relaxed">
                                    {u.text}
                                </p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* B2B Lead Magnet Form */}
            <section
                id="ficha-tecnica"
                className="py-20 md:py-28 relative overflow-hidden"
                style={{ backgroundColor: line.color }}
            >
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.08] bg-hex-pattern pointer-events-none"
                    style={{ backgroundImage: "url('/brand/pattern-3.png')" }}
                />
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="rounded-2xl bg-white p-6 md:p-12 shadow-pop grid md:grid-cols-2 gap-10">
                        <div>
                            <span className="inline-flex items-center gap-2 rounded-full bg-brand-cream px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-brand-ink">
                                <FileText className="h-3.5 w-3.5" /> Para profissionais
                            </span>
                            <h2 className="mt-5 font-display font-medium text-3xl md:text-4xl text-brand-ink leading-tight">
                                Pedir ficha técnica completa da Linha {line.name}
                            </h2>
                            <p className="mt-4 text-brand-ink-soft leading-relaxed">
                                Envia-nos os teus dados e recebes em 24h: ficha técnica detalhada,
                                preços por formato, amostra gratuita e condições comerciais para{" "}
                                {line.bestFor.slice(0, 2).join(" e ").toLowerCase()}.
                            </p>
                            <ul className="mt-6 space-y-2 text-sm">
                                {[
                                    "Ficha técnica + ficha nutricional detalhada",
                                    "Tabela de preços por formato",
                                    "Amostra gratuita sob pedido",
                                    "Condições comerciais B2B",
                                ].map((i) => (
                                    <li key={i} className="flex items-center gap-2 text-brand-ink-soft">
                                        <Check className={`h-4 w-4 ${line.accentClass}`} /> {i}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {done ? (
                            <div className="flex flex-col items-center justify-center text-center p-6 rounded-2xl bg-brand-green-soft">
                                <div className="h-16 w-16 rounded-full bg-brand-green flex items-center justify-center">
                                    <Check className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="mt-5 font-display font-medium text-2xl text-brand-ink">
                                    Recebido!
                                </h3>
                                <p className="mt-2 text-brand-ink-soft">
                                    Vais receber a ficha técnica da Linha {line.name} no teu email
                                    em menos de 24h.
                                </p>
                                <Link
                                    to="/"
                                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-ink text-white px-5 py-2.5 text-sm font-semibold"
                                    data-testid={`line-${line.id}-done-back`}
                                >
                                    <ArrowLeft className="h-4 w-4" /> Voltar ao início
                                </Link>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-3">
                                <Field
                                    label="Nome *"
                                    testid={`line-${line.id}-input-name`}
                                    value={form.name}
                                    onChange={handle("name")}
                                    placeholder="O teu nome"
                                    required
                                />
                                <Field
                                    label="Email profissional *"
                                    type="email"
                                    testid={`line-${line.id}-input-email`}
                                    value={form.email}
                                    onChange={handle("email")}
                                    placeholder="email@empresa.pt"
                                    required
                                />
                                <div className="grid grid-cols-2 gap-3">
                                    <Field
                                        label="Telefone"
                                        testid={`line-${line.id}-input-phone`}
                                        value={form.phone}
                                        onChange={handle("phone")}
                                        placeholder="+351 …"
                                    />
                                    <Field
                                        label="Cidade"
                                        testid={`line-${line.id}-input-city`}
                                        value={form.city}
                                        onChange={handle("city")}
                                        placeholder="Porto"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-brand-ink mb-1.5 block">
                                        Tipo de negócio
                                    </label>
                                    <select
                                        value={form.business_type}
                                        onChange={handle("business_type")}
                                        data-testid={`line-${line.id}-input-business`}
                                        className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-base text-brand-ink focus:outline-none focus:border-brand-green"
                                    >
                                        <option value="">Seleciona...</option>
                                        {line.bestFor.map((b) => (
                                            <option key={b} value={b}>
                                                {b}
                                            </option>
                                        ))}
                                        <option value="Outro">Outro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-brand-ink mb-1.5 block">
                                        Mensagem (opcional)
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={form.message}
                                        onChange={handle("message")}
                                        data-testid={`line-${line.id}-input-message`}
                                        placeholder="Volume estimado, prazos, dúvidas..."
                                        className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-base text-brand-ink focus:outline-none focus:border-brand-green"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid={`line-${line.id}-submit`}
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink hover:bg-brand-green text-white px-6 py-3.5 text-sm font-semibold transition-all shadow-pop disabled:opacity-60"
                                >
                                    {loading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Send className="h-4 w-4" />
                                    )}
                                    {loading ? "A enviar..." : "Enviar e receber ficha"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* Other lines */}
            <section className="py-16 md:py-20 bg-brand-cream">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-baseline justify-between flex-wrap gap-3">
                        <h2 className="font-display font-medium text-2xl md:text-3xl text-brand-ink">
                            Explorar outras linhas
                        </h2>
                        <Link
                            to="/#linhas"
                            className="text-sm font-bold text-brand-green hover:underline"
                        >
                            Ver todas →
                        </Link>
                    </div>
                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {LINE_IDS.filter((id) => id !== line.id).map((id) => {
                            const other = LINES[id];
                            return (
                                <Link
                                    key={id}
                                    to={`/linhas/${id}`}
                                    data-testid={`other-line-${id}`}
                                    className="group rounded-2xl bg-white p-5 shadow-soft hover:shadow-pop transition-all hover:-translate-y-1"
                                >
                                    <div
                                        className={`h-10 w-10 rounded-xl ${other.bgClass} flex items-center justify-center`}
                                    >
                                        <Package className={`h-5 w-5 ${other.accentClass}`} />
                                    </div>
                                    <div className="mt-4 font-display font-medium text-xl text-brand-ink">
                                        {other.name}
                                    </div>
                                    <div className="text-xs text-brand-ink-soft mt-1">
                                        {other.tagline}
                                    </div>
                                    <div
                                        className={`mt-3 inline-flex items-center gap-1 text-xs font-bold ${other.accentClass}`}
                                    >
                                        Ver linha <ArrowRight className="h-3 w-3" />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            <Footer />
            <WhatsAppButton />
            <CartDrawer />
        </div>
    );
}

const Field = ({ label, testid, ...rest }) => (
    <div>
        <label className="text-sm font-semibold text-brand-ink mb-1.5 block">{label}</label>
        <input
            data-testid={testid}
            className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-base text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:border-brand-green"
            {...rest}
        />
    </div>
);
